import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiFetch } from "../../api/client";
import { useAppUser } from "../../app/AppUserContext";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import { PATHS } from "../../app/paths";

type NominationStatus = "Extracted" | "InReview" | "Committed" | "Rejected" | "Superseded";

interface NominationRow {
  nominationId: number;
  fileName: string;
  producerNameRaw: string | null;
  status: NominationStatus;
  parseStatus: string;
  fieldsNeedingReview: number;
  receivedUtc: string;
  createdByAppUserId: number;
  resinFamilyRaw: string | null;
  directionCode: string | null;
  amountCentsPerLb: number | null;
  effectiveDate: string | null;
  regionCode: string | null;
}

const STATUS_CHIP: Record<NominationStatus, string> = {
  Extracted: "info",
  InReview: "info",
  Committed: "ok",
  Rejected: "bad",
  Superseded: "bad",
};

const IN_REVIEW_STATUSES: NominationStatus[] = ["Extracted", "InReview"];

export function PriceNominationsTab() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { appRole, appUserId } = useAppUser();
  const canManage = (createdByAppUserId: number) =>
    appRole === "Admin" || (appRole === "Uploader" && createdByAppUserId === appUserId);
  const { confirm, dialog } = useConfirmDialog();

  const [rows, setRows] = useState<NominationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [producerFilter, setProducerFilter] = useState("");
  const [resinFilter, setResinFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("nominations/queue?status=All");
      setRows(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (row: NominationRow) => {
    const reason = await confirm({
      title: "Remove nomination",
      message: `Remove the ${row.producerNameRaw ?? row.fileName} nomination?`,
      reasonLabel: "Reason (shown in the audit trail)",
    });
    if (reason === null) return;
    try {
      await apiFetch(`nominations/${row.nominationId}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || "Removed from Resin8 Data View" }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteRejected = async (row: NominationRow) => {
    const result = await confirm({
      title: "Delete nomination",
      message: `Permanently delete the rejected ${row.producerNameRaw ?? row.fileName} nomination? This cannot be undone.`,
    });
    if (result === null) return;
    try {
      await apiFetch(`nominations/${row.nominationId}`, { method: "DELETE" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const filtered = rows.filter((r) => {
    if (producerFilter && !(r.producerNameRaw ?? "").toLowerCase().includes(producerFilter.toLowerCase())) return false;
    if (resinFilter && !(r.resinFamilyRaw ?? "").toLowerCase().includes(resinFilter.toLowerCase())) return false;
    if (directionFilter && r.directionCode !== directionFilter) return false;
    if (dateFrom && (!r.effectiveDate || r.effectiveDate < dateFrom)) return false;
    if (dateTo && (!r.effectiveDate || r.effectiveDate > dateTo)) return false;
    return true;
  });

  const selectable = filtered.filter((r) => canManage(r.createdByAppUserId));
  const allSelected = selectable.length > 0 && selectable.every((r) => selectedIds.has(r.nominationId));

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      selectable.forEach((r) => next.add(r.nominationId));
      return next;
    });
  };

  const bulkDelete = async () => {
    const selectedRows = rows.filter((r) => selectedIds.has(r.nominationId));
    if (!selectedRows.length) return;
    const toDelete = selectedRows.filter((r) => r.status === "Rejected");
    const toReject = selectedRows.filter((r) => r.status !== "Rejected");

    const reason = await confirm({
      title: "Remove/delete selected nominations",
      message:
        `This affects ${selectedRows.length} nomination(s): ${toDelete.length} already-rejected ` +
        `one(s) will be permanently deleted, and ${toReject.length} other(s) will be removed.`,
      reasonLabel: toReject.length > 0 ? "Reason for removal (shown in the audit trail)" : undefined,
    });
    if (reason === null) return;

    const confirmAgain = await confirm({
      title: "Are you sure?",
      message: `This cannot be undone. Confirm removing/deleting ${selectedRows.length} nomination(s)?`,
      confirmLabel: "Yes, delete",
    });
    if (confirmAgain === null) return;

    try {
      await Promise.all([
        ...toDelete.map((r) => apiFetch(`nominations/${r.nominationId}`, { method: "DELETE" })),
        ...toReject.map((r) =>
          apiFetch(`nominations/${r.nominationId}/reject`, {
            method: "POST",
            body: JSON.stringify({ reason: reason || "Bulk removed from Resin8 Data View" }),
          }),
        ),
      ]);
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
    <section className="lane on">
      <h2>Price Nominations</h2>
      <p className="lede">Every producer price nomination letter received, extracted, and its current review/commit status.</p>

      <div className="sec">
        <div className="card">
          <div className="inline">
            <input className="ctl" placeholder="Filter by producer…" value={producerFilter} onChange={(e) => setProducerFilter(e.target.value)} />
            <input className="ctl" placeholder="Filter by resin…" value={resinFilter} onChange={(e) => setResinFilter(e.target.value)} />
            <select className="ctl" value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}>
              <option value="">Any direction</option>
              <option value="Increase">Increase</option>
              <option value="Decrease">Decrease</option>
              <option value="Hold">Hold</option>
              <option value="Withdraw">Withdraw</option>
            </select>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
              Effective from
              <input className="ctl" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--grey)" }}>
              Effective to
              <input className="ctl" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="sec"><div className="gate narrow"><span className="msg" style={{ color: "var(--salmon)" }}>{error}</span></div></div>
      )}

      {selectedIds.size > 0 && (
        <div className="sec">
          <div className="card" style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{selectedIds.size} selected</span>
            <button className="btn danger" onClick={bulkDelete}>
              <span className="material-symbols-sharp">delete_forever</span>Remove/Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="sec">
        <div className="card" style={{ padding: 0, overflow: "auto", maxWidth: "100%" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 1 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0} />
                </th>
                <th>Producer</th>
                <th>File</th>
                <th>Resin</th>
                <th>Direction</th>
                <th>Amount (¢/lb)</th>
                <th>Effective</th>
                <th>Region</th>
                <th>Status</th>
                <th>Received</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={11}>No nominations match these filters.</td></tr>}
              {filtered.map((r) => {
                const inReview = IN_REVIEW_STATUSES.includes(r.status);
                return (
                  <tr
                    key={r.nominationId}
                    style={inReview ? { cursor: "pointer" } : undefined}
                    title={inReview ? "Continue reviewing this letter" : undefined}
                    onClick={inReview ? () => navigate(`${PATHS.intakeLane("nom")}?open=${r.nominationId}`) : undefined}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.nominationId)}
                        disabled={!canManage(r.createdByAppUserId)}
                        onChange={() => toggleOne(r.nominationId)}
                      />
                    </td>
                    <td>{r.producerNameRaw ?? "—"}</td>
                    <td>{r.fileName}</td>
                    <td>{r.resinFamilyRaw ?? "—"}</td>
                    <td>{r.directionCode ?? "—"}</td>
                    <td>{r.amountCentsPerLb ?? "—"}</td>
                    <td>{r.effectiveDate ?? "—"}</td>
                    <td>{r.regionCode ?? "—"}</td>
                    <td><span className={`chip ${STATUS_CHIP[r.status]}`}>{r.status}</span></td>
                    <td>{new Date(r.receivedUtc).toLocaleString()}</td>
                    <td>
                      {r.status === "Rejected" ? (
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.createdByAppUserId)}
                          title={!canManage(r.createdByAppUserId) ? "You can only delete nominations you uploaded, unless you're an Admin" : "Permanently delete this rejected nomination"}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteRejected(r);
                          }}
                        >
                          <span className="material-symbols-sharp">delete_forever</span>
                        </button>
                      ) : (
                        <button
                          className="btn ghost"
                          disabled={!canManage(r.createdByAppUserId)}
                          title={!canManage(r.createdByAppUserId) ? "You can only remove nominations you uploaded, unless you're an Admin" : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(r);
                          }}
                        >
                          <span className="material-symbols-sharp">delete</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
    {dialog}
    </>
  );
}
